import React, { useEffect, useState } from 'react';
import { Container, Box, Alert, useTheme, useMediaQuery } from '@mui/material';
import { MainLayout } from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Dashboard,
  UserManagement,
  UsageStatistics,
  AIUsageLogs,
  SystemSettings,
  SponsorManagement,
} from '../components/Admin';

// AIManagementPageコンポーネントの型定義
type AIManagementComponent = React.ComponentType<any>;

type AdminView =
  | 'dashboard'
  | 'users'
  | 'statistics'
  | 'ai-logs'
  | 'settings'
  | 'sponsors'
  | 'ai-management';

// パスからビューを特定する関数
const getViewFromPath = (path: string): AdminView => {
  if (path.includes('/admin/users')) return 'users';
  if (path.includes('/admin/statistics')) return 'statistics';
  if (path.includes('/admin/ai-logs')) return 'ai-logs';
  if (path.includes('/admin/settings')) return 'settings';
  if (path.includes('/admin/sponsors')) return 'sponsors';
  if (path.includes('/admin/ai-management')) return 'ai-management';
  if (path.includes('/admin/dashboard')) return 'dashboard';

  return 'dashboard';
};

export const AdminPage: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [AIManagementPage, setAIManagementPage] =
    useState<AIManagementComponent | null>(null);
  const currentView = getViewFromPath(location.pathname);

  // AIManagementPageの動的インポート
  useEffect(() => {
    const loadAIManagementPage = async () => {
      try {
        const module = await import('../pages/AIManagementPage');
        setAIManagementPage(() => module.default);
      } catch (error) {
        console.warn('AIManagementPageのインポートに失敗しました。', error);
      }
    };

    if (currentView === 'ai-management') {
      loadAIManagementPage();
    }
  }, [currentView]);

  // 管理者権限チェック
  if (!user || user.userRole !== 'admin') {
    return (
      <MainLayout>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <Alert severity="error">
            アクセス権限がありません。管理者のみアクセス可能です。
          </Alert>
        </Container>
      </MainLayout>
    );
  }

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'users':
        return <UserManagement />;
      case 'statistics':
        return <UsageStatistics />;
      case 'ai-logs':
        return <AIUsageLogs />;
      case 'settings':
        return <SystemSettings />;
      case 'sponsors':
        return <SponsorManagement />;
      case 'ai-management':
        // AI管理ページがあれば表示、なければダッシュボードを表示
        return AIManagementPage ? (
          <AIManagementPage />
        ) : (
          <Dashboard onNavigate={handleNavigate} />
        );
      case 'dashboard':
      default:
        return <Dashboard onNavigate={handleNavigate} />;
    }
  };

  return (
    <MainLayout
      showHeader={isMobile}
      showFooter={false}
      maxWidth={false}
      title="ガクフドキャリア"
    >
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ minHeight: '100vh' }}>{renderCurrentView()}</Box>
      </Container>
    </MainLayout>
  );
};
